import React from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useStoreState } from "ezStore";

import { Checkbox, FormGroup } from "@cofano/cck";
import { Grid2 as Grid, Typography } from "@mui/material";

import { Fields, hasOptionalFieldPermission, hasPluginPermission, Language, usePermission } from "imports/Utils";

import CharterSubtypeSelect from "view/charters/components/selects/CharterSubtypeSelect";
import RelationSelect from "view/relations/components/selects/RelationSelect";
import ShipOwnerSelect from "view/ships/components/selects/ShipOwnerSelect";
import { ShipSchemaInput, ShipSchemaOutput } from "view/ships/utils/ship.schema";
import ShipSelect from "view/ships/components/selects/ShipSelect";

import { RelationType } from "__generated__/enums";

const ShipGeneralForm = () => {
	const session = useStoreState(store => store.session);

	const { control, setValue } = useFormContext<ShipSchemaInput, unknown, ShipSchemaOutput>();

	const [ skipper, shipManager, shipOwner ] = useWatch({ control, name: [ "skipper", "shipManager", "shipOwner" ] });

	const { isRTR, isMasterdata, isReadOnly } = usePermission();

	const canEdit = (isRTR || isMasterdata) && !isReadOnly;

	return (
		<React.Fragment>
			<Grid container spacing={3}>
				<Grid size={{ lg: 6, md: 6, xs: 12 }}>
					<FormGroup label={Language("SHIP_MANAGER")} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="shipManager"
							control={control}
							render={({ field }) => (
								<RelationSelect
									{...field}
									onChange={option => {
										field.onChange(option);
										if (shipOwner?.id && shipOwner.id === shipManager?.id) {
											setValue("shipOwner", option);
										}
									}}
									disabled={!canEdit}
									types={[ RelationType.ShipManager ]} />
							)} />
					</FormGroup>

					<FormGroup label={Language("SKIPPER")} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="skipper"
							control={control}
							render={({ field }) => (
								<RelationSelect
									{...field}
									types={[ RelationType.Skipper ]}
									getOptionDisabled={option => !!option.skipperShip}
									disabled />
							)} />

						{!skipper && (
							<Typography variant="caption" color="error">
								{Language("SHIP_ADD_NEW_RELATION_WILL_BE_CREATED")}
							</Typography>
						)}
					</FormGroup>

					<FormGroup label={Language("RELATION_SHIP_OWNER")} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="shipOwner"
							control={control}
							render={({ field }) => (
								<ShipOwnerSelect
									value={field.value}
									onChange={option => field.onChange(option?.id ? option : null)}
									disabled={!canEdit}
									skipper={skipper ?? null}
									shipManager={shipManager ?? null} />
							)} />
					</FormGroup>

					<FormGroup label={Language("OPERATOR")} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="operator"
							control={control}
							render={({ field }) => (
								<RelationSelect
									{...field}
									disabled={!canEdit}
									types={[ RelationType.Operator ]} />
							)} />
					</FormGroup>

					{hasOptionalFieldPermission(session, Fields.SHIP, "coupledShip") && (
						<FormGroup label={Language("COUPLED_SHIP")} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="coupledShip"
								control={control}
								render={({ field }) => <ShipSelect {...field} />} />
						</FormGroup>
					)}

					{hasOptionalFieldPermission(session, Fields.SHIP, "defaultCharterType") && (
						<FormGroup label={Language("CHARTER_TYPE")} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="defaultCharterSubtype"
								control={control}
								render={({ field }) => <CharterSubtypeSelect {...field} />} />
						</FormGroup>
					)}

					<FormGroup label={Language("SHIPS_RETRIEVE_ACTUALS")} labelPlacement="left-center" overflow="hidden">
						<Controller
							name="retrieveActuals"
							control={control}
							render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
					</FormGroup>

					{hasPluginPermission(session, "showLastShippedProducts") && (
						<FormGroup label={Language("SHIP_SHOW_LAST_SHIPPED_PRODUCTS")} labelPlacement="left-center" overflow="visible">
							<Controller
								name="showLastProducts"
								control={control}
								render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
						</FormGroup>
					)}

					{hasOptionalFieldPermission(session, Fields.SHIP, "tracking") && (
						<FormGroup label={Language("SHIP_TRACKING")} labelPlacement="left-center" overflow="hidden">
							<Controller
								name="tracking"
								control={control}
								render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
						</FormGroup>
					)}

					{hasOptionalFieldPermission(session, Fields.SHIP, "advancedTracking") && (
						<FormGroup label={Language("SHIPS_ADVANCED_TRACKING")} labelPlacement="left-center" overflow="visible">
							<Controller
								name="advancedTracking"
								control={control}
								render={({ field }) => <Checkbox {...field} value={undefined} checked={field.value} />} />
						</FormGroup>
					)}
				</Grid>
			</Grid>
		</React.Fragment>
	);
};

export default ShipGeneralForm;
